import { consentsSelector } from '@/features/configuration/configurationSelector';
import { useSelector } from '@/store/utils';
import { sanitizeMarkup } from '@/utils/sanitizeMarkup';
import { Checkbox, Typography } from '@packages/webshop-design-system';
import { type FC, useEffect, useMemo, useState } from 'react';

const TICKET_CANCELLATION_ACTION = 'CUSTOMER_ACTION.TICKET_CANCELLATION';

interface RefundConsentsProps {
  // Fires on mount too, so a tenant with no cancellation consents immediately
  // reports `true` and the caller's confirm button is never left disabled.
  onAcceptanceChange: (allAccepted: boolean) => void;
}

const RefundConsents: FC<RefundConsentsProps> = ({ onAcceptanceChange }) => {
  const consents = useSelector(consentsSelector);

  const requiredConsents = useMemo(
    () =>
      consents?.items
        ?.filter(({ requiredForCustomerActions }) =>
          requiredForCustomerActions?.some(
            (action) => action.id === TICKET_CANCELLATION_ACTION
          )
        )
        .map(({ id, description, name }) => ({
          id,
          text: description || name || '',
        })) || [],
    [consents?.items]
  );

  const [acceptedConsentsMap, setAcceptedConsentsMap] = useState<
    Record<number, boolean>
  >({});

  const allAccepted = requiredConsents.every(
    ({ id }) => acceptedConsentsMap[id]
  );

  useEffect(() => {
    onAcceptanceChange(allAccepted);
  }, [allAccepted, onAcceptanceChange]);

  if (!requiredConsents.length) {
    return null;
  }

  return (
    <div className="flex flex-col justify-start gap-1">
      {requiredConsents.map((consent) => (
        <Checkbox
          key={consent.id}
          size="lg"
          id={`consent-${consent.id}`}
          isSelected={acceptedConsentsMap[consent.id]}
          classNames={{
            checkbox:
              'group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-tint group-data-[focus-visible]:ring-offset-2',
          }}
          onChange={(checked) =>
            setAcceptedConsentsMap((prev) => ({
              ...prev,
              [consent.id]: checked,
            }))
          }
        >
          <Typography
            variant="body1"
            className="[&_a]:text-primary [&_a]:underline hover:[&_a]:text-primary-dark"
            dangerouslySetInnerHTML={{
              __html: sanitizeMarkup(consent.text),
            }}
          />
        </Checkbox>
      ))}
    </div>
  );
};

export default RefundConsents;
